import { useEffect, useRef } from 'react'

import { type MessageDescriptor } from 'react-intl'

import { toast } from '@webapp/components/Notification'
import { useProcurifyIntl } from '@webapp/hooks'
import { useQueryPOBillToContact } from '@webapp/modules/purchasing/features/get-po-bill-to-settings/models/useQueryPOBillToContact'
import { useQueryPOBillToContactUser } from '@webapp/modules/purchasing/features/get-po-bill-to-settings/models/useQueryPOBillToContactUser'
import { useQueryPOBillToPrimaryCategory } from '@webapp/modules/purchasing/features/get-po-bill-to-settings/models/useQueryPOBillToPrimaryCategory'
import { POBillToContactModes } from '@webapp/platform/iam'
import messages from './messages'

// Toasts once per error episode: the flag resets when the error clears, so a
// later failure toasts again, but re-renders while it persists do not.
const useErrorToastOnce = (
  isError: boolean,
  title: MessageDescriptor,
  message: MessageDescriptor
) => {
  const intl = useProcurifyIntl()
  const hasShownToast = useRef(false)

  useEffect(() => {
    if (!isError) {
      hasShownToast.current = false
      return
    }

    if (hasShownToast.current) return
    hasShownToast.current = true

    toast.error({
      title: intl.formatMessage(title),
      children: intl.formatMessage(message),
    })
  }, [isError, intl, title, message])
}

/**
 * Reads the two `po-bill-to-*` system preferences for the Bill To defaults
 * card. A GET 404 (preference never set) maps to the default for each key,
 * never an error (BHV-004).
 *
 * Any other failure reading a preference is surfaced as `isError` (and
 * toasted once): the exposed `primaryCategory`/`contact` are then either
 * stale or the defaults, never a confirmed read, and the Feature Set must
 * not let a debounced save persist them over the tenant's real values.
 * A failed lookup of the stored
 * specific user is surfaced the same way as `isContactUserError`, since an
 * empty picker would otherwise be indistinguishable from a settled one.
 *
 * The stored specific-user's name is fetched separately and kept out of
 * `isLoading`: folding its (re-)fetch into the aggregate would unmount the
 * Bill To fields — and cancel any pending debounced save — every time a
 * specific-user save resolves and re-keys the by-id lookup. Callers should
 * mount as soon as `isLoading` clears and let the user picker show its own
 * loading state via `isLoadingContactUser` while the name arrives.
 */
export const usePOBillToSettingsPresenter = () => {
  const {
    value: primaryCategory,
    isLoading: isLoadingPrimaryCategory,
    isError: isPrimaryCategoryError,
  } = useQueryPOBillToPrimaryCategory()
  const {
    value: contact,
    isLoading: isLoadingContact,
    isError: isContactError,
  } = useQueryPOBillToContact()

  const hasStoredContactUser =
    contact.mode === POBillToContactModes.SPECIFIC_USER &&
    contact.userId != null

  const {
    contactUser,
    isLoading: isLoadingContactUser,
    isError: isContactUserError,
  } = useQueryPOBillToContactUser(hasStoredContactUser ? contact.userId : null)

  const isError = isPrimaryCategoryError || isContactError

  useErrorToastOnce(
    isError,
    messages.preferencesFetchErrorTitle,
    messages.preferencesFetchErrorMessage
  )
  useErrorToastOnce(
    isContactUserError,
    messages.contactUserFetchErrorTitle,
    messages.contactUserFetchErrorMessage
  )

  return {
    primaryCategory,
    contact,
    contactUser: hasStoredContactUser ? contactUser : null,
    isLoadingContactUser: hasStoredContactUser && isLoadingContactUser,
    isLoading: isLoadingPrimaryCategory || isLoadingContact,
    isError,
    isContactUserError: hasStoredContactUser && isContactUserError,
  }
}
